import { useEffect, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import "../../pages/stripe/CheckoutForm.css";
import { Button } from "@app/components";
import { getOrderDetailsById, makeOrderPayment, placeOrder } from "@app/services/order";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { clearCart, setStep } from "@app/store/reducers/order";
import { useNavigate, useParams } from "react-router-dom";
import CurrencyFormat from 'react-currency-format';
import ReCAPTCHA from 'react-google-recaptcha';
import { Alert } from "react-bootstrap";

const CheckoutForm = () => {

    let { id } = useParams();
	let obj: any = {}
	// Setup state to track client secret, errors and checkout status
	const [succeeded, setSucceeded] = useState(false);
	const [error, setError] = useState(null);
	const [apiError, setAPIError] = useState(null);
	const [processing, setProcessing] = useState(false);
	const [disabled, setDisabled] = useState(true);
    const [order, setOrderData] = useState(obj);
	const franchise = useSelector((state: any) => state.order.franchise);
	const cartAmount = useSelector((state: any) => state.order.cart.amount);
	const currentUserId = useSelector((state: any) => state.auth.currentUser.id);
	const selectedRole = useSelector((state: any) => state.auth.selectedRole.slug);
    const dispatch = useDispatch();
    const navigate = useNavigate();
	const [showAlert, setShowAlert] = useState(true);
	const [cardInfo, setCardInfo] = useState({
		name: "",
		expiry: "",
		number: "",
		address: {
			line: "",
			postalCode: "",
		},
	});
	const [captchaToken, setCaptchaToken] = useState(null);

	// Store reference to Stripe
	const stripe = useStripe();
	const elements = useElements();

	useEffect(() => {
		if(id){
			getOrderById(id);
		}
    }, []);

    const getOrderById = async (id) => {
        try {
            setProcessing(true);
            const resp = await getOrderDetailsById(id);
            if(resp?.success === true){
                setProcessing(false);
				if(!(resp?.collections?.order_status == 'unpaid' || resp?.collections?.order_status == 'failed')){
					toast.error('something went wrong, please try again!');
					navigate('/in-progress-orders');
				}
                setOrderData(resp?.collections);
            }else{
                setProcessing(false);
                toast.error(resp?.message);
                navigate('/in-progress-orders');
            }
        } catch (error: any) {
            setProcessing(false);
            toast.error(error?.message || 'Failed');
        }
    };

	const handleChange = async (event) => {
		// Listen for changes in the CardElement and display any errors as the customer types their card details
		setDisabled(event.empty);
		setError(event.error ? event.error.message : "");
	};

	const handleChangeAddressLine = (e) => {
		const { value } = e.target;
		setCardInfo((prev) => {
		  	return { ...prev, address: { ...prev.address, line: value } };
		});
	}

	const handleChangeName = (e) => {
		const { value } = e.target;
		setCardInfo((prev) => {
		  	return { ...prev, name: value };
		});
	}

	const handleSubmit = async (ev) => {
		ev.preventDefault();
		// if (!captchaToken) {
		// 	setError("Please complete the captcha");
		// 	return;
		// }

		setProcessing(true);
		const address = cardInfo.address;
		const billingDetails = {
			name: cardInfo.name,
			address: {
				line1: address.line,
			},
		};
		
		try{
			await stripe.createPaymentMethod({
				type: "card",
				billing_details: billingDetails,
				card: elements.getElement(CardElement),
			}) .then( async (resp) => {
				if(resp.paymentMethod){
					setError(null);
					const paymentMethod = resp.paymentMethod;
					const payload = {
							contact:franchise?.phone, 
							address:franchise?.address, 
							default_order:franchise?.default_order, 
							default_launchpack_order:franchise?.default_launchpack_order, 
							handbook_binding:franchise?.handbook_binding,
							instructions:franchise?.instructions,
							payment_method_id: paymentMethod?.id,
							amount: cartAmount,
							// captchaToken: captchaToken
						}
						await id ? makePaymentAPI({order_id: order?.id, payment_method_id: paymentMethod?.id}) : placeOrderAPI(payload);
					
				}else{
					setError(resp?.error?.message);
					setDisabled(true);
					setProcessing(false);
				}
			}).catch((err) => {
				console.log('Promise catch: ', err.error.message);
			});
		} catch (err) {
			setError(`Payment failed: ${err.error.message}`)
			setDisabled(true);
			setProcessing(false);
		}
	};

	const placeOrderAPI = async (payload) => {
		setProcessing(true);
		const res = await placeOrder(franchise?.id, payload);
		if(res?.success === true){
			if(res?.collections?.status === "requires_action"){
				const { paymentIntent, error } = await stripe.confirmCardPayment(
					res.collections.client_secret
				);
				if (error){
					setProcessing(false);
					toast.error(error?.message, {autoClose:7000});
					setAPIError(error?.message);
					return;
				} 
				if (paymentIntent.status === "succeeded"){
					payload.payment_intent_id = paymentIntent.id;
					placeOrderAPI(payload);
				}else{
					setProcessing(false);
					toast.error('We are unable to authenticate your payment method. Please choose a different payment method and try again.', {autoClose:7000});
					setAPIError('We are unable to authenticate your payment method. Please choose a different payment method and try again.');
				}
			}else{
				setSucceeded(true);
				setProcessing(false);
				toast.success(res?.message);
				dispatch(setStep(1));
				dispatch(clearCart());
				navigate('/orders/view/'+ res?.collections?.id);
			}
		}else{
			setProcessing(false);
			toast.error(res?.errors[0]?.msg, {autoClose:7000});
			setAPIError(res?.errors[0]?.msg);
			// dispatch(setStep(4));
		}
	}

	const makePaymentAPI = async (payload) => {
		const res = await makeOrderPayment(payload);
		if(res?.success === true){
			if(res?.collections?.status === "requires_action"){
				const { paymentIntent, error } = await stripe.confirmCardPayment(
					res.collections.client_secret
				);
				if (error){
					setProcessing(false);
					toast.error(error?.message, {autoClose:7000});
					setAPIError(error?.message);
					return;
				} 
				if (paymentIntent.status === "succeeded"){
					payload.payment_intent_id = paymentIntent.id;
					makePaymentAPI(payload);
				}else{
					setProcessing(false);
					toast.error('We are unable to authenticate your payment method. Please choose a different payment method and try again.', {autoClose:7000});
					setAPIError('We are unable to authenticate your payment method. Please choose a different payment method and try again.');
				}
			}else{
				setSucceeded(true);
				setProcessing(false);
				toast.success(res?.message);
				navigate('/orders/view/'+ res?.collections?.id);
			}
		}else{
			setProcessing(false);
			setDisabled(true);
			toast.error(res?.errors[0]?.msg, {autoClose:7000});
			setAPIError(res?.errors[0]?.msg);
		}
	}

	const paylater = async () => {
		const payload = {
			contact:franchise?.phone, 
			address:franchise?.address, 
			default_order:franchise?.default_order, 
			default_launchpack_order:franchise?.default_launchpack_order,
			handbook_binding:franchise?.handbook_binding,
			instructions:franchise?.instructions,
			payment_method_id: 'paylater',
			amount: cartAmount
		}
		await placeOrderAPI(payload);
	}

	const handleCancel = (e) => {
		id ? navigate('/in-progress-orders') : dispatch(setStep(4));
	}

	const handleCaptchaChange = (token) => {
    	setCaptchaToken(token);
  	};

	return (
		<>
		<div className="payWrapper" id="payWrapper">
			<p className="text-danger">{ apiError }</p>
			<form id="payment-form" onSubmit={handleSubmit}>
			<div className="innerWrapper">
				<div className="title">Make Payment</div>
				{showAlert && (
					<Alert className="d-flex align-items-start" variant="secondary" dismissible onClose={() => setShowAlert(false)} >
						<small>
							<i className="fas fa-info-circle mr-2"/>
							<b>Note: </b>Your bank may open a 3D Secure pop-up window. Please allow pop-ups and redirects for this site in your browser settings.
						</small>
					</Alert>
				)}
				<div className="row pl-2">
					<label>Name On Card</label>
					<input
						onChange={handleChangeName}
						type="text"
						name="name"
						placeholder="Enter card holder name"
					/>
				</div>
				<label>Card Details</label>
				<div className="rowPaymentInput">
					<CardElement
						id="card-element"
						options={{}}
						onChange={handleChange}
					/>
				</div>
				<div className="addressWrapper">
					<div className="row pl-2">
						<label>Full Address</label>
						<input
							onChange={handleChangeAddressLine}
							type="text"
							name="address"
							placeholder="Enter full address"
						/>
					</div>
					{/* <ReCAPTCHA
						sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
						onChange={handleCaptchaChange}
					/> */}
					<div className="btnContainer">
						<Button type="button" isLoading={processing} onClick={ handleCancel } className="btn btn-danger mr-2"> Cancel </Button>
						{ !id && currentUserId == 1 && selectedRole === 'super-admin' && (
							<Button type="button" isLoading={processing} onClick={ paylater } className="btn btn-success mr-2"> Pay Later </Button>
						)}
						{/* { !id && (
							<Button type="button" isLoading={processing} onClick={ paylater } className="btn btn-success mr-2"> Pay Later </Button>
						)} */}
						<Button id="submit" type="submit" isLoading={processing} className="btn btn-primary mr-2" disabled={processing || disabled || succeeded}> Pay <CurrencyFormat value={ id ? (parseFloat(order?.total_price) + parseFloat(order?.pcharge_total)).toFixed(2) : cartAmount.toFixed(2) } decimalScale={2} displayType={'text'} thousandSeparator={true} prefix={'£'} /></Button>
					
						{/* <button disabled={processing || disabled || succeeded} id="submit">Pay</button> */}
					</div>
					{error && (
						<div className="text-danger" role="alert">{error}</div>
					)}
					<p className={succeeded ? "text-success" : "text-success d-none"}>Payment succeeded!</p>
				</div>
			</div>
			</form>
			<div className="mt-2" id="3d-secure-wrapper"></div>
		</div>
		</>
	);
};

export default CheckoutForm;